import { storableError } from '../../util/errors';
import { getSuperhostProgress } from '../../util/api';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { fetchExternalBookings } from '../../ducks/externalBookings.duck';
import { PROVIDER } from '../../util/types';
import { isInterioresListing } from '../../util/searchListingTabs';
import {
  VALID_RESERVATION_TRANSITIONS,
  INQUIRE_TRANSITION,
  PENDING_RESERVATION_TRANSITIONS,
  indexIncludedBookings,
  isBookingStillUpcoming,
  isPendingReservation,
  queryAllTransactionPages,
  resolveBookingStart,
  shouldListInquiry,
} from '../../util/bookingTimeline';

// ================ Action types ================ //

export const FETCH_RESERVATIONS_REQUEST = 'app/DashboardPage/FETCH_RESERVATIONS_REQUEST';
export const FETCH_RESERVATIONS_SUCCESS = 'app/DashboardPage/FETCH_RESERVATIONS_SUCCESS';
export const FETCH_RESERVATIONS_ERROR = 'app/DashboardPage/FETCH_RESERVATIONS_ERROR';
export const FETCH_SUPERHOST_PROGRESS_SUCCESS =
  'app/DashboardPage/FETCH_SUPERHOST_PROGRESS_SUCCESS';
export const FETCH_INDOOR_SPACES_PROMO_SUCCESS =
  'app/DashboardPage/FETCH_INDOOR_SPACES_PROMO_SUCCESS';

// ================ Reducer ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const initialState = {
  fetchInProgress: false,
  fetchReservationsError: null,
  transactionRefs: [],
  pendingTransactionRefs: [],
  // { criteria, metrics } from /api/superhost/me, or null (none, or not loaded).
  superhostProgress: null,
  // Published listings that can take an indoor space (pools and gardens, not interiores):
  // [{ id, title }]. Empty hides the "espacios interiores" notice.
  indoorSpacesPromoListings: [],
};

export default function dashboardPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_RESERVATIONS_REQUEST:
      return { ...state, fetchInProgress: true, fetchReservationsError: null };
    case FETCH_RESERVATIONS_SUCCESS:
      return {
        ...state,
        fetchInProgress: false,
        transactionRefs: entityRefs(payload.upcoming.data.data),
        pendingTransactionRefs: entityRefs(payload.pending.data.data),
      };
    case FETCH_RESERVATIONS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchInProgress: false, fetchReservationsError: payload };
    case FETCH_SUPERHOST_PROGRESS_SUCCESS:
      return { ...state, superhostProgress: payload };
    case FETCH_INDOOR_SPACES_PROMO_SUCCESS:
      return { ...state, indoorSpacesPromoListings: payload };
    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchReservationsRequest = () => ({ type: FETCH_RESERVATIONS_REQUEST });
const fetchReservationsSuccess = payload => ({
  type: FETCH_RESERVATIONS_SUCCESS,
  payload,
});
const fetchReservationsError = e => ({
  type: FETCH_RESERVATIONS_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

// The superhost table on the dashboard. Fire-and-forget and fail-safe: a host with
// no progress (404), no database or any error just gets the informational card.
export const fetchSuperhostProgress = () => dispatch =>
  getSuperhostProgress()
    .then(progress => {
      const valid = progress && progress.criteria && progress.metrics;
      dispatch({ type: FETCH_SUPERHOST_PROGRESS_SUCCESS, payload: valid ? progress : null });
    })
    .catch(() => {
      dispatch({ type: FETCH_SUPERHOST_PROGRESS_SUCCESS, payload: null });
    });

// The "espacios interiores" notice: shown to hosts with a published pool or garden, not
// to those who only publish interiores (an indoor space is an add-on to an outdoor one).
// One ownListings.query per dashboard visit. Fail-safe: any error just hides the notice.
export const fetchIndoorSpacesPromoListings = () => (dispatch, getState, sdk) =>
  sdk.ownListings
    .query({
      states: ['published'],
      perPage: 100,
      'fields.listing': ['title', 'publicData.searchListingType', 'publicData.category'],
    })
    .then(response => {
      const listings = (response.data.data || [])
        .filter(l => !isInterioresListing(l))
        .map(l => ({ id: l.id.uuid, title: l.attributes.title }));
      dispatch({ type: FETCH_INDOOR_SPACES_PROMO_SUCCESS, payload: listings });
    })
    .catch(() => {
      dispatch({ type: FETCH_INDOOR_SPACES_PROMO_SUCCESS, payload: [] });
    });

// Short previews on the dashboard; full lists live on HoldListingsPage / UpcomingListingsPage.
const DASHBOARD_UPCOMING_SIZE = 5;
const DASHBOARD_PENDING_SIZE = 5;

const sharedTransactionFields = {
  include: ['listing', 'customer', 'customer.profileImage', 'booking'],
  'fields.transaction': [
    'processName',
    'lastTransition',
    'lastTransitionedAt',
    'payinTotal',
    'payoutTotal',
    'lineItems',
    'metadata',
    'protectedData',
  ],
  'fields.listing': ['title', 'availabilityPlan', 'publicData.listingType'],
  'fields.booking': ['start', 'end', 'displayStart', 'displayEnd', 'state'],
  'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
  'fields.image': ['variants.square-small', 'variants.square-small2x'],
  perPage: 100,
};

const keepUpcoming = response => {
  const raw = response.data.data || [];
  const includedBookingsByUuid = indexIncludedBookings(response);
  const now = new Date();
  const data = raw
    .filter(rawTx => {
      const isInquire = rawTx?.attributes?.lastTransition === INQUIRE_TRANSITION;
      if (isInquire) {
        return (
          shouldListInquiry(rawTx?.attributes?.metadata) &&
          isBookingStillUpcoming(rawTx, includedBookingsByUuid, now)
        );
      }
      return isBookingStillUpcoming(rawTx, includedBookingsByUuid, now);
    })
    .sort((a, b) => {
      const startA = resolveBookingStart(a, includedBookingsByUuid);
      const startB = resolveBookingStart(b, includedBookingsByUuid);
      if (!startA && !startB) return 0;
      if (!startA) return 1;
      if (!startB) return -1;
      return startA.getTime() - startB.getTime();
    })
    .slice(0, DASHBOARD_UPCOMING_SIZE);
  return {
    data: {
      data,
      included: response.data.included,
      meta: { ...response.data.meta, totalItems: data.length },
    },
  };
};

const keepPending = response => {
  const raw = response.data.data || [];
  const data = raw
    .filter(rawTx => isPendingReservation(rawTx?.attributes?.lastTransition))
    .sort((a, b) => {
      const atA = a?.attributes?.lastTransitionedAt;
      const atB = b?.attributes?.lastTransitionedAt;
      if (!atA && !atB) return 0;
      if (!atA) return 1;
      if (!atB) return -1;
      return new Date(atB).getTime() - new Date(atA).getTime();
    })
    .slice(0, DASHBOARD_PENDING_SIZE);
  return {
    data: {
      data,
      included: response.data.included,
      meta: { ...response.data.meta, totalItems: data.length },
    },
  };
};

export const loadData = () => (dispatch, getState, sdk) => {
  const userMaybe = getState().user.currentUser;
  const userReady = userMaybe ? Promise.resolve() : dispatch(fetchCurrentUser());

  return userReady.then(() => {
    const user = getState().user.currentUser;
    const isProvider = user?.attributes?.profile?.publicData?.userType === PROVIDER;

    // Customers have no sales to preview; skip the queries for them.
    if (!isProvider) {
      return null;
    }

    // Tells the "Proteger reserva externa" banner whether the feature is available.
    // Fire-and-forget: the thunk swallows its own errors, so a missing database can
    // never break the dashboard.
    dispatch(fetchExternalBookings());
    dispatch(fetchSuperhostProgress());
    dispatch(fetchIndoorSpacesPromoListings());

    dispatch(fetchReservationsRequest());

    const upcomingQuery = {
      only: 'sale',
      lastTransitions: VALID_RESERVATION_TRANSITIONS,
      sort: 'bookingStart',
      ...sharedTransactionFields,
    };

    const pendingQuery = {
      only: 'sale',
      lastTransitions: PENDING_RESERVATION_TRANSITIONS,
      sort: '-bookingStart',
      ...sharedTransactionFields,
    };

    return Promise.all([
      // Every page: manual reservations have no booking and sort last, so on page 1
      // they would only appear for hosts with fewer than 100 bookings.
      queryAllTransactionPages(sdk, upcomingQuery, { expand: true }),
      sdk.transactions.query(pendingQuery, { expand: true }),
    ])
      .then(([upcomingResponse, pendingResponse]) => {
        const upcoming = keepUpcoming(upcomingResponse);
        const pending = keepPending(pendingResponse);

        dispatch(addMarketplaceEntities(upcoming));
        dispatch(addMarketplaceEntities(pending));

        const payload = { upcoming, pending };
        dispatch(fetchReservationsSuccess(payload));
        return payload;
      })
      .catch(e => {
        dispatch(fetchReservationsError(storableError(e)));
        throw e;
      });
  });
};
